import { Button, ButtonProps, Flex, FlexProps } from '@chakra-ui/react';
import { ReactNode } from 'react';

import { pxToRem } from '../../theme';
import { AppSpinner } from '../Spinner';
import { FormWithTxStage } from './types';

type ButtonWithSpinnerSharedProps = Omit<ButtonProps, 'onClick'> & {
  label?: ReactNode;
  onClick?: () => void;
};

export type FormButtonsEditingProps = {
  onClickCancel: () => void;
  isDisabledNext: boolean;
  setStage: (stage: FormWithTxStage) => void;
  labelPrev?: string;
  propsContainer?: FlexProps;
};

export type FormButtonsWaiting4ConfirmationProps = {
  stage: FormWithTxStage;
  setStage: (stage: FormWithTxStage) => void;
  onClickNext?: () => void;
  onClickPrev?: () => void;
  label?: string;
  propsContainer?: FlexProps;
};

export type FormButtonWithApprovalProps = {
  stage: FormWithTxStage;
  confirm?: () => void;
  approve?: () => void;
  hasEnoughAllowance: boolean;
  txHash?: `0x${string}`;
  label?: string;
  labelApprove?: string;
};

export function ButtonWithSpinner({
  isSpinning,
  label,
  onClick,
  ...rest
}: ButtonWithSpinnerSharedProps & { isSpinning: boolean }) {
  return (
    <Button
      cursor={isSpinning ? 'default' : 'pointer'}
      minWidth={pxToRem(97)}
      onClick={isSpinning ? undefined : onClick}
      disabled={!onClick}
      {...rest}
    >
      {isSpinning ? <AppSpinner /> : label}
    </Button>
  );
}

export function FormButtonApprove({
  txHash,
  label,
  ...rest
}: ButtonWithSpinnerSharedProps & {
  txHash?: `0x${string}`;
}) {
  const isSpinning = txHash !== undefined;

  return <ButtonWithSpinner isSpinning={isSpinning} label={label || 'Approve'} {...rest} />;
}

export function FormButtonWithSpinner<T>({
  stage,
  stageSpinner,
  ...rest
}: ButtonWithSpinnerSharedProps & {
  stage: T;
  stageSpinner: T | T[];
}) {
  const isSpinning = Array.isArray(stageSpinner)
    ? stageSpinner.some((stageSpinnerItem) => stageSpinnerItem === stage)
    : stage === stageSpinner;

  return <ButtonWithSpinner isSpinning={isSpinning} {...rest} />;
}

export function FormButtonWithApproval({
  stage,
  hasEnoughAllowance,
  txHash,
  approve,
  label,
  labelApprove,
  confirm,
}: FormButtonWithApprovalProps) {
  return hasEnoughAllowance ? (
    <FormButtonWithSpinner
      stage={stage}
      stageSpinner="inProgress"
      onClick={confirm}
      label={label || 'Confirm'}
    />
  ) : (
    <FormButtonApprove label={labelApprove} txHash={txHash} onClick={approve} />
  );
}

export function FormButtonsContainer({ css, ...props }: FlexProps) {
  return (
    <Flex
      justify="end"
      gap={4}
      mt={4}
      {...props}
      css={{
        button: {
          width: ['50%', 'auto'],

          '&:only-child': {
            width: ['100%', 'auto'],
          },
        },
        ...css,
      }}
    />
  );
}

export function FormButtonsWithSummaryContainer(props: FlexProps) {
  return (
    <Flex
      justify={['initial', 'space-between']}
      align={['flex-end', 'center']}
      flexDirection={['column', 'row']}
      gap={4}
      mt={4}
      {...props}
      css={{
        '& > div:last-child': {
          mt: 0,
          w: ['100%', 'auto'],
        },
      }}
    />
  );
}

export function FormButtonsEditing({
  onClickCancel,
  isDisabledNext,
  setStage,
  labelPrev,
  propsContainer,
}: FormButtonsEditingProps) {
  return (
    <FormButtonsContainer {...propsContainer}>
      <Button variant="systemSecondary" onClick={onClickCancel}>
        {labelPrev || 'Cancel'}
      </Button>
      <Button disabled={isDisabledNext} onClick={() => setStage('waiting4Confirmation')}>
        Next
      </Button>
    </FormButtonsContainer>
  );
}

export function FormButtonsWithApproval({
  setStage,
  ...rest
}: {
  setStage: (stage: FormWithTxStage) => void;
} & FormButtonWithApprovalProps) {
  return (
    <FormButtonsContainer>
      <Button variant="systemSecondary" onClick={() => setStage('editing')}>
        Back
      </Button>
      <FormButtonWithApproval {...rest} />
    </FormButtonsContainer>
  );
}

export function FormButtonsWaiting4Confirmation({
  stage,
  setStage,
  onClickNext,
  onClickPrev,
  label,
  propsContainer,
}: FormButtonsWaiting4ConfirmationProps) {
  return (
    <FormButtonsContainer {...propsContainer}>
      <Button
        variant="systemSecondary"
        onClick={onClickPrev ? onClickPrev : () => setStage('editing')}
      >
        {label || 'Back'}
      </Button>
      <FormButtonWithSpinner
        stage={stage}
        stageSpinner="inProgress"
        onClick={onClickNext}
        label="Confirm"
      />
    </FormButtonsContainer>
  );
}
